import {ChangeDetectionStrategy, Component, DestroyRef, effect, Injectable, input, isDevMode, signal} from "@angular/core";
import {DokumentId, DokumentRessursClient, DokumentSignatarerModell, DokumentsigneringRessursClient, MoetestatusModell, MøteId, MøteModellEnkel, MøteRessursClient, SignatarStatus, SigneringStatus, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erMøteAvholdt} from "felles-ng2/modell/møteModellHjelpere";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {MatButton} from "@angular/material/button";
import {TranslateModule} from "@ngx-translate/core";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {C3TodeltPaiKomponent} from "web-ng2/delt/komponenter/c3-todelt-pai/c3-todelt-pai";
import {MatTooltip} from "@angular/material/tooltip";
import {verifiserErIkkeNil} from "felles-ng2/støttefunksjoner/antakelser";
import {MøteDatatjeneste} from "felles-ng2/tjenester/datalager/MøteDatatjeneste";
import {firstValueFrom, Observable} from "rxjs";
import {MøtePubliserDialogKomponent} from "web-ng2/moduler/møte/modale/møte-publiser-dialog/møte-publiser-dialog";
import {MatDialog} from "@angular/material/dialog";
import {kunRequestRespons, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {MatIcon} from "@angular/material/icon";
import {NedtrekksmenyKomponent} from "web-ng2/delt/komponenter/nedtrekksmeny/nedtrekksmeny";
import {NedtrekksmenyTriggerForDirektiv} from "web-ng2/delt/komponenter/nedtrekksmeny/nedtrekksmeny-trigger-for";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {SigneringStatusKomponent} from "web-ng2/moduler/signering/komponenter/signering-status/signering-status";
import {MatProgressBar} from "@angular/material/progress-bar";
import {MøteprotokollSendTilSigneringDialogKomponent} from "web-ng2/moduler/møteprotokoll/modale/møteprotokoll-send-til-signering-dialog/møteprotokoll-send-til-signering-dialog";
import {SigneringSignerDialogKomponent} from "web-ng2/moduler/signering/modale/signering-signer-dialog/signering-signer-dialog";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {dialogRespons} from "felles-ng2/infrastruktur/material/matDialogHjelpere";
import {pieKonfigBlaa, pieKonfigGroenn} from "web-ng2/moduler/signering/komponenter/signering-status/signeringStatusPieHjelpere";
import {tomDokumentSignatarerModell} from "felles-ng2/modell/tommeModeller";

const debug = initDebugger('MøteprotokollStatusKomponent');

enum Status {
    laster_data,
    moete_ikke_avholdt,
    moete_ikke_publisert,
    protokoll_ikke_publisert,
    protokoll_kan_sendes_til_signering,
    protokoll_under_arbeid,
    protokoll_skal_signeres_av_bruker,
    protokoll_venter_paa_signaturer,
    protokoll_signert_men_ikke_konvoluttert,
    protokoll_signert
}

/**
 * Status for møteprotokoll
 */
@Component({
    selector: 'moeteprotokoll-status',
    templateUrl: 'møteprotokoll-status.html',
    styleUrl: 'møteprotokoll-status.scss',
    imports: [
        MatButton,
        TranslateModule,
        C3TodeltPaiKomponent,
        MatTooltip,
        MatIcon,
        NedtrekksmenyKomponent,
        NedtrekksmenyTriggerForDirektiv,
        SigneringStatusKomponent,
        MatProgressBar
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollStatusKomponent {

    moete = input.required<MøteModellEnkel>();

    signatarer = signal<DokumentSignatarerModell>(tomDokumentSignatarerModell())
    status = signal(Status.laster_data);
    laster = asynkArbeidPågårSignal();

    statuser = Status;
    pieKonfigBlaa = pieKonfigBlaa;
    pieKonfigGroenn = pieKonfigGroenn;

    moetestatus: MoetestatusModell | null = null;

    constructor(private avhengigheter: MøteprotokollStatusKomponentAvhengigheter,
                private klokke: Klokke,
                private kontekst: BrukerkontekstTilbyder,
                private destroyRef: DestroyRef) {
        effect(async () => this.status.set(await this.beregnStatus()));
    }

    signatarerOversiktAapnet() {
        const arbeid = this.laster.start();
        this.avhengigheter.signatarer(this.moete().protokolldokumentId)
          .pipe(arbeid.avsluttVedLeveranse(), takeUntilDestroyed(this.destroyRef))
          .subscribe(signatarer => this.signatarer.set(signatarer));
    }

    async publiserMoete() {
        await this.avhengigheter.publiserMøte(this.moete().id);
    }

    async publiserProtokoll() {
        await this.avhengigheter.publiserProtokoll(this.moete().id);
    }

    async sendProtokollTilSignering() {
        await this.avhengigheter.sendProtokollTilSignering(this.moete().id);
    }

    async signerProtokoll() {
        await this.avhengigheter.signer(this.moete().id, this.moete().protokolldokumentId);
    }

    //private hjelpemetoder

    /**
     * Beregner status for møtet.
     * Merk at vi ved beregning av status ikke tar høyde for om bruker er skjermet,
     * feks vil publisering av møte feile dersom bruker har skjermet tilgang til møtet, og vi sjekker ikke her om
     * bruker har skjermet tilgang (ville betinge at vi lastet full møtemodell).
     */
    private async beregnStatus(): Promise<Status> {
        const avholdt = erMøteAvholdt(this.moete(), this.klokke);

        //aktuelle status før møte avholdes
        if (!this.moete().erMoetePublisert)
            return Status.moete_ikke_publisert;

        //protokoll kan i teorien sendes til signering før møtet er avholdt
        if (this.moete().erProtokollPublisert) {
            const status = await this.protokollrelatertStatus();
            if (erIkkeNil(status)) return status;
        }

        if (!avholdt)
            return Status.moete_ikke_avholdt;

        //møte er nå publisert og avholdt
        if (!this.moete().erProtokollPublisert)
            return Status.protokoll_ikke_publisert;

        //protokoll er nå publisert, og må være et sted i signeringsløpet
        const status = await this.protokollrelatertStatus();
        isDevMode() && verifiserErIkkeNil(status)

        return status!;
    }

    private async protokollrelatertStatus(): Promise<Status | null> {
        const brukerKanRedigere = this.kontekst.autentisertBruker().inneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.moete().eiendeGruppeId);

        if (this.moete().protokollSigneringStatus == SigneringStatus.ikkeAktivert)
            return brukerKanRedigere ? Status.protokoll_kan_sendes_til_signering : Status.protokoll_under_arbeid;

        //protokoll har nå blitt sendt til signering eller ferdig signert

        if ([SigneringStatus.tilSignering, SigneringStatus.delvisSignert].includes(this.moete().protokollSigneringStatus))
            return (await this.skalSigneresAvBruker()) ? Status.protokoll_skal_signeres_av_bruker : Status.protokoll_venter_paa_signaturer

        //protokoll er ferdig signert men muligens ikke konvoluttert
        if (this.moete().protokollSigneringStatus == SigneringStatus.signert)
            return Status.protokoll_signert_men_ikke_konvoluttert;

        if ([SigneringStatus.signertOgKonvoluttert, SigneringStatus.eksterntSignert].includes(this.moete().protokollSigneringStatus))
            return Status.protokoll_signert;

        return null;
    }

    private async skalSigneresAvBruker(): Promise<boolean> {
        await this.lastMøtestatus();
        return this.moetestatus!.protokollSigneringsstatusForBruker == SignatarStatus.skalSignere
    }

    private async lastMøtestatus(): Promise<void> {
        this.moetestatus = await this.laster.utfør(() => this.avhengigheter.møtestatus(this.moete().id));
    }
}

@Injectable()
export class MøteprotokollStatusKomponentAvhengigheter {

    constructor(private møteDatatjeneste: MøteDatatjeneste,
                private møteRessurs: MøteRessursClient,
                private dokumentRessurs: DokumentRessursClient,
                private dokumentsigneringRessurs: DokumentsigneringRessursClient,
                private matDialog: MatDialog) {
    }

    møtestatus(id: MøteId): Promise<MoetestatusModell> {
        return this.møteRessurs.hentStatus(id).somPromise();
    }

    signatarer(protokolldokumentId: DokumentId): Observable<DokumentSignatarerModell> {
        return this.dokumentsigneringRessurs.signatarer(protokolldokumentId).somObservable()
    }

    async publiserMøte(id: MøteId) {
        await dialogRespons(MøtePubliserDialogKomponent.visPubliserMøte(this.matDialog, this.møtepromise(id)));
    }

    async publiserProtokoll(id: MøteId) {
        await dialogRespons(MøtePubliserDialogKomponent.visPubliserProtokoll(this.matDialog, this.møtepromise(id)));
    }

    async sendProtokollTilSignering(id: MøteId) {
        await dialogRespons(MøteprotokollSendTilSigneringDialogKomponent.vis(this.matDialog, this.møtepromise(id)));
    }

    /** Viser signeringsdialog og returnerer true dersom bruker signerte */
    async signer(id: MøteId, protokolldokumentId: DokumentId): Promise<boolean> {
        const dialog = SigneringSignerDialogKomponent.vis(this.matDialog,
          this.dokumentRessurs.hentGjeldendeDokumentversjon(protokolldokumentId).somPromise(),
          this.dokumentsigneringRessurs.signatarinformasjonForAktivBruker(protokolldokumentId).somPromise());

        const signertOk = await dialogRespons(dialog, false);
        //sørg for å relaste møte dersom bruker signerte, slik at protokollstatus blir oppdatert i gui
        if (signertOk) await this.møteDatatjeneste.relast(id);

        return signertOk;
    }

    //private hjelpemetoder

    private møtepromise(id: string) {
        return firstValueFrom(this.møteDatatjeneste.hentMedMeta(id).pipe(kunRequestRespons(), mapTilData()));
    }

}
